import React from 'react';
import * as R from 'ramda';
import { element, number, func, string, shape, arrayOf } from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ButtonIcon } from '../icon';
import Select, { SelectItem } from '../select';
import Toolbar, { ToolbarRow, ToolbarSection } from '../toolbar';
import { ALIGN_START, ALIGN_END } from '../toolbar/section';

const DEFAULT_LIMITS = [10, 50, 100, 250, 500];

const DEFAULT_LIMIT = 10;
const DEFAULT_OFFSET = 0;

const defaultMessages = defineMessages({
  fromToOf: {
    id: 'pagination.fromToOf',
    defaultMessage: '{from}-{to} of {total}',
  },
  itemsPerPage: {
    id: 'pagination.itemsPerPage',
    defaultMessage: 'Items per page',
  },
});

const Pagination = ({
  itemsPerPage, limit, offset, total, onChange, children, limits,
}) => {
  const prev = () => onChange({ limit, offset: offset - limit });
  const next = () => onChange({ limit, offset: offset + limit });
  const changeLimit = limit$ => onChange({ limit: limit$, offset: 0 });
  const from = R.inc(offset);
  const to = offset + limit > total ? total : offset + limit;

  return (
    <Toolbar className="pagination">
      <ToolbarRow>
        <ToolbarSection align={ALIGN_START}>
          {children}
        </ToolbarSection>
        <ToolbarSection align={ALIGN_END} shrink>
          <div className="mdc-toolbar__text">
            <label htmlFor="items-per-page">
              <FormattedMessage {...itemsPerPage} />
            </label>
          </div>
          <div className="mdc-toolbar__text">
            <Select top={false} value={limit} onChange={changeLimit} id="items-per-page">
              {limits.map(limit$ => <SelectItem key={limit$} value={limit$}>{limit$}</SelectItem>)}
            </Select>
          </div>
        </ToolbarSection>
        <ToolbarSection align={ALIGN_END} shrink>
          <div className="mdc-toolbar__text">
            <FormattedMessage values={{ from, to, total }} {...defaultMessages.fromToOf} />
          </div>
        </ToolbarSection>
        <ToolbarSection align={ALIGN_END} shrink>
          <ButtonIcon
            onClick={prev}
            disabled={offset <= 0}
            className="mdc-toolbar__icon"
          >
            keyboard_arrow_left
          </ButtonIcon>
          <ButtonIcon
            onClick={next}
            disabled={offset + limit >= total}
            className="mdc-toolbar__icon"
          >
            keyboard_arrow_right
          </ButtonIcon>
        </ToolbarSection>
      </ToolbarRow>
    </Toolbar>
  );
};

Pagination.propTypes = {
  children: element,
  limit: number,
  offset: number,
  total: number.isRequired,
  limits: arrayOf(number),
  onChange: func,
  itemsPerPage: shape({
    id: string,
    defaultMessage: string,
  }),
};

Pagination.defaultProps = {
  children: null,
  limit: DEFAULT_LIMIT,
  offset: DEFAULT_OFFSET,
  onChange: () => null,
  itemsPerPage: defaultMessages.itemsPerPage,
  limits: DEFAULT_LIMITS,
};

export default Pagination;
